import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { makeStyles } from '@fluentui/react-components';
import Card from '../../common/card/Card';
import FeatureFlagged from '../../common/featureFlagged/featureFlagged';
import SpanWithLabel from '../../common/SpanWithLabel';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import { bodyContentContainer } from '../../../app/common/styles/CommonStyleObjects';
import { GET_INTAKE_REQUEST } from '../../../utils/statApi/EntityTerminationApi';
import FeatureFlagContext from '../../../utils/featureFlags/FeatureFlagContext';
import momentUtc from '../../../utils/DateFormatter';
import StatDateFormats from '../../../utils/types/DateFormats';
import { IIntakeRequestQueryResult } from '../types';

interface IEntityTerminationIntakePageProps {
  loading?: boolean;
  companyCode: string;
}

const useStyles = makeStyles({
  cardRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '20px',
    marginBottom: '50px',
  },
  cardColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexBasis: 0,
    minWidth: '250px',
    '& > div': { flexGrow: 1 },
  },
  detailsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  },
  detailsColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexBasis: 0,
    minWidth: '250px',
    gap: '15px',
  },
});

const EntityTerminationIntakePage = ({
  loading,
  companyCode,
}: IEntityTerminationIntakePageProps): JSX.Element => {
  const classes = useStyles();
  const featureFlags = useContext(FeatureFlagContext);
  const {
    data,
    loading: intakeLoading,
    error,
  } = useQuery<IIntakeRequestQueryResult, { companyCode: string }>(GET_INTAKE_REQUEST, {
    variables: { companyCode },
    fetchPolicy: 'cache-and-network',
    skip: !featureFlags.flags.EntityTerminationIntake,
  });
  const request = data?.intakeRequest;
  const isLoading = !!loading || intakeLoading;

  return (
    <FeatureFlagged flagName="EntityTerminationIntake">
      <div className={`${bodyContentContainer} lightBackground`}>
        {error && <LoadingErrorMessage loading={isLoading} error={error} />}
        <div className={classes.cardRow}>
          <div className={classes.cardColumn}>
            <Card title="Intake Request Details" isLoading={isLoading} headingLevel={2}>
              {request ? (
                <div className={classes.detailsRow}>
                  <div className={classes.detailsColumn}>
                    <SpanWithLabel
                      labelText="Created By:"
                      value={`${request.createdBy?.displayName} (${request.createdBy?.alias})`}
                    />
                    <SpanWithLabel
                      labelText="Created On:"
                      value={
                        request.createdOn
                          ? momentUtc(request.createdOn, StatDateFormats.DayWithTime, true)
                          : undefined
                      }
                    />
                    <SpanWithLabel
                      labelText="Business Reason:"
                      value={request.businessReason}
                      spanOverrideStyles={{ whiteSpace: 'pre-wrap' }}
                    />
                  </div>
                  <div className={classes.detailsColumn}>
                    <SpanWithLabel labelText="Are there employees?" value={request.hasEmployees} />
                    <SpanWithLabel
                      labelText="Are there revenue contracts?"
                      value={request.hasRevenueContracts}
                    />
                    <SpanWithLabel
                      labelText="Are there other operating expenses?"
                      value={request.hasOperatingExpenses}
                    />
                  </div>
                </div>
              ) : (
                !error && !isLoading && <p>No intake request found.</p>
              )}
            </Card>
          </div>
        </div>
      </div>
    </FeatureFlagged>
  );
};

export default EntityTerminationIntakePage;
